import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Collapse,
  Drawer,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Switch,
  TimePicker,
  message,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  PRICE_CALCULATIONS,
  ShippingRate,
  ShippingTypeOption,
  TimeLimit,
  WEEKDAY_LABELS,
} from "./types";
import { createShippingRate, listShippingTypes, updateShippingRate } from "./api";

const { Panel } = Collapse;
const { TextArea } = Input;

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  sellerCode: string;
  initialRate?: ShippingRate | null;
}

interface FormShape {
  enabled: boolean;
  title: string;
  description?: string;
  minPrice: number;
  shippingZoneRaw?: string;
  totalFrom?: number;
  totalTo?: number;
  weightFrom?: number;
  weightTo?: number;
  volumeFrom?: number;
  volumeTo?: number;
  maxWeight?: number;
  maxVolume?: number;
  priceCalculation: string;
  orderStart?: number;
  orderStep?: number;
  orderValue?: number;
  distanceStart?: number;
  distanceStep?: number;
  distanceValue?: number;
  weightStart?: number;
  weightStep?: number;
  weightValue?: number;
  volumeStart?: number;
  volumeStep?: number;
  volumeValue?: number;
  minHoursDisplay?: number;
  maxDaysToDisplay?: number;
  maxSimultaneousToDisplay?: number;
  shippingTypeIds: number[];
  timeLimits: { day: number; from: dayjs.Dayjs; to: dayjs.Dayjs; position?: string }[];
}

export const RateFormDrawer: React.FC<Props> = ({
  open,
  onClose,
  onSaved,
  sellerCode,
  initialRate,
}) => {
  const [form] = Form.useForm<FormShape>();
  const [submitting, setSubmitting] = useState(false);
  const [shippingTypes, setShippingTypes] = useState<ShippingTypeOption[]>([]);
  const isEdit = !!initialRate?.id;

  useEffect(() => {
    if (!open) return;
    listShippingTypes()
      .then(setShippingTypes)
      .catch(() => setShippingTypes([]));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (initialRate) {
      form.setFieldsValue({
        ...initialRate,
        shippingZoneRaw: initialRate.shippingZone
          ? JSON.stringify(initialRate.shippingZone, null, 2)
          : "",
        timeLimits: (initialRate.timeLimits ?? []).map((tl) => ({
          day: tl.day,
          from: tl.from ? dayjs(tl.from, "HH:mm") : dayjs("09:00", "HH:mm"),
          to: tl.to ? dayjs(tl.to, "HH:mm") : dayjs("18:00", "HH:mm"),
          position: tl.position,
        })),
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        enabled: true,
        priceCalculation: "MAX",
        minPrice: 0,
        shippingTypeIds: [],
        timeLimits: [],
      });
    }
  }, [open, initialRate, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      let shippingZone: Record<string, unknown> | null = null;
      if (values.shippingZoneRaw && values.shippingZoneRaw.trim()) {
        try {
          shippingZone = JSON.parse(values.shippingZoneRaw);
        } catch {
          message.error("La zona de envío no es JSON válido");
          setSubmitting(false);
          return;
        }
      }

      const timeLimits: TimeLimit[] = (values.timeLimits ?? []).map((tl) => ({
        day: tl.day,
        from: tl.from?.format("HH:mm"),
        to: tl.to?.format("HH:mm"),
        position: tl.position,
      }));

      const payload = {
        enabled: values.enabled,
        title: values.title,
        description: values.description,
        minPrice: Number(values.minPrice ?? 0),
        shippingZone,
        totalFrom: nullableNumber(values.totalFrom),
        totalTo: nullableNumber(values.totalTo),
        weightFrom: nullableNumber(values.weightFrom),
        weightTo: nullableNumber(values.weightTo),
        volumeFrom: nullableNumber(values.volumeFrom),
        volumeTo: nullableNumber(values.volumeTo),
        maxWeight: nullableNumber(values.maxWeight),
        maxVolume: nullableNumber(values.maxVolume),
        priceCalculation: values.priceCalculation,
        orderStart: nullableNumber(values.orderStart),
        orderStep: nullableNumber(values.orderStep),
        orderValue: nullableNumber(values.orderValue),
        distanceStart: nullableNumber(values.distanceStart),
        distanceStep: nullableNumber(values.distanceStep),
        distanceValue: nullableNumber(values.distanceValue),
        weightStart: nullableNumber(values.weightStart),
        weightStep: nullableNumber(values.weightStep),
        weightValue: nullableNumber(values.weightValue),
        volumeStart: nullableNumber(values.volumeStart),
        volumeStep: nullableNumber(values.volumeStep),
        volumeValue: nullableNumber(values.volumeValue),
        minHoursDisplay: nullableNumber(values.minHoursDisplay),
        maxDaysToDisplay: nullableNumber(values.maxDaysToDisplay),
        maxSimultaneousToDisplay: nullableNumber(values.maxSimultaneousToDisplay),
        timeLimits,
        shippingTypeIds: (values.shippingTypeIds ?? []).map((v) => Number(v)).filter((n) => !Number.isNaN(n)),
      };

      if (isEdit && initialRate) {
        await updateShippingRate(sellerCode, initialRate.id, payload);
        message.success("Rate actualizado");
      } else {
        await createShippingRate(sellerCode, payload);
        message.success("Rate creado");
      }
      onSaved();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || "Error al guardar";
      message.error(Array.isArray(msg) ? msg.join(" • ") : msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      title={isEdit ? `Editar rate #${initialRate?.id}` : "Nuevo rate"}
      width={720}
      open={open}
      onClose={onClose}
      destroyOnClose
      extra={
        <Space>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="primary" loading={submitting} onClick={handleSubmit}>
            Guardar
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical">
        <Collapse defaultActiveKey={["identidad", "filtros", "pricing", "delivery"]} bordered={false}>
          <Panel header="Identidad" key="identidad">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Habilitado" name="enabled" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Cálculo de precio"
                  name="priceCalculation"
                  rules={[{ required: true }]}
                >
                  <Select options={PRICE_CALCULATIONS.map((v) => ({ value: v, label: v }))} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label="Título" name="title" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item label="Descripción" name="description">
              <Input />
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Precio mínimo"
                  name="minPrice"
                  rules={[{ required: true, type: "number", min: 0 }]}
                >
                  <InputNumber style={{ width: "100%" }} min={0} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Tipos de carga"
                  name="shippingTypeIds"
                  tooltip="Seleccioná los tipos de carga que cubre este rate"
                >
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="Elegí uno o más tipos"
                    options={shippingTypes.map((t) => ({ value: t.id, label: t.label }))}
                    optionFilterProp="label"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Panel>

          <Panel header="Filtros y zona" key="filtros">
            <Form.Item
              label="Zona (GeoJSON Polygon/MultiPolygon)"
              name="shippingZoneRaw"
              tooltip='Ej: {"type":"Polygon","coordinates":[[[lng,lat], …]]}'
            >
              <TextArea rows={5} placeholder='{"type":"Polygon","coordinates":[[…]]}' />
            </Form.Item>
            <RangeRow label="Total" prefix="total" />
            <RangeRow label="Peso (kg)" prefix="weight" />
            <RangeRow label="Volumen (m³)" prefix="volume" />
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Vehículo: max peso (kg)" name="maxWeight">
                  <InputNumber style={{ width: "100%" }} min={0} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Vehículo: max volumen (m³)" name="maxVolume">
                  <InputNumber style={{ width: "100%" }} min={0} />
                </Form.Item>
              </Col>
            </Row>
          </Panel>

          <Panel header="Reglas de pricing" key="pricing">
            <PriceRow label="Orden total" prefix="order" />
            <PriceRow label="Distancia (km)" prefix="distance" />
            <PriceRow label="Peso (kg)" prefix="weight" />
            <PriceRow label="Volumen (m³)" prefix="volume" />
          </Panel>

          <Panel header="Delivery date" key="delivery">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="Mín. horas para mostrar" name="minHoursDisplay">
                  <InputNumber style={{ width: "100%" }} min={0} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Máx. días a mostrar" name="maxDaysToDisplay">
                  <InputNumber style={{ width: "100%" }} min={0} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Máx. simultáneos" name="maxSimultaneousToDisplay">
                  <InputNumber style={{ width: "100%" }} min={0} />
                </Form.Item>
              </Col>
            </Row>
            <Form.List name="timeLimits">
              {(fields, { add, remove }) => (
                <div>
                  {fields.map((field) => (
                    <Row key={field.key} gutter={8} align="middle" style={{ marginBottom: 8 }}>
                      <Col span={7}>
                        <Form.Item
                          {...field}
                          name={[field.name, "day"]}
                          label={field.name === 0 ? "Día" : ""}
                          rules={[{ required: true }]}
                          style={{ marginBottom: 0 }}
                        >
                          <Select
                            options={WEEKDAY_LABELS.map((label, value) => ({ value, label }))}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={7}>
                        <Form.Item
                          {...field}
                          name={[field.name, "from"]}
                          label={field.name === 0 ? "Desde" : ""}
                          rules={[{ required: true }]}
                          style={{ marginBottom: 0 }}
                        >
                          <TimePicker format="HH:mm" minuteStep={15} style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                      <Col span={7}>
                        <Form.Item
                          {...field}
                          name={[field.name, "to"]}
                          label={field.name === 0 ? "Hasta" : ""}
                          rules={[{ required: true }]}
                          style={{ marginBottom: 0 }}
                        >
                          <TimePicker format="HH:mm" minuteStep={15} style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                      <Col span={3} style={{ display: "flex", alignItems: "center" }}>
                        <Button
                          type="text"
                          icon={<DeleteOutlined />}
                          onClick={() => remove(field.name)}
                          danger
                        />
                      </Col>
                    </Row>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() =>
                      add({ day: 0, from: dayjs("09:00", "HH:mm"), to: dayjs("18:00", "HH:mm") })
                    }
                    icon={<PlusOutlined />}
                    block
                  >
                    Agregar franja horaria
                  </Button>
                </div>
              )}
            </Form.List>
          </Panel>
        </Collapse>
      </Form>
    </Drawer>
  );
};

const RangeRow: React.FC<{ label: string; prefix: "total" | "weight" | "volume" }> = ({
  label,
  prefix,
}) => (
  <Row gutter={16}>
    <Col span={12}>
      <Form.Item label={`${label} desde`} name={`${prefix}From`}>
        <InputNumber style={{ width: "100%" }} min={0} />
      </Form.Item>
    </Col>
    <Col span={12}>
      <Form.Item label={`${label} hasta`} name={`${prefix}To`}>
        <InputNumber style={{ width: "100%" }} min={0} />
      </Form.Item>
    </Col>
  </Row>
);

const PriceRow: React.FC<{
  label: string;
  prefix: "order" | "distance" | "weight" | "volume";
}> = ({ label, prefix }) => (
  <Row gutter={16}>
    <Col span={8}>
      <Form.Item label={`${label} — start`} name={`${prefix}Start`}>
        <InputNumber style={{ width: "100%" }} />
      </Form.Item>
    </Col>
    <Col span={8}>
      <Form.Item label={`${label} — step`} name={`${prefix}Step`}>
        <InputNumber style={{ width: "100%" }} />
      </Form.Item>
    </Col>
    <Col span={8}>
      <Form.Item label={`${label} — value`} name={`${prefix}Value`}>
        <InputNumber style={{ width: "100%" }} />
      </Form.Item>
    </Col>
  </Row>
);

const nullableNumber = (v: unknown): number | undefined => {
  if (v === null || v === undefined || v === "") return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};
